import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from 'sonner';

import { PauseButtonIcon, PlayButtonIcon } from '../../../../components';
import { Card } from '../../../../components/surface';
import { loadDiagnosis } from '../../record';
import { useTakeMedia } from '../../server';

import type { AnalyStereo, TakeMedia } from '../../server';

/**
 * 결과 종합의 "내 녹음 다시 듣기" — 서버에 올라간 테이크를 한 줄씩 재생한다.
 *
 * **조회가 실패하면 카드가 없다**(자리·에러 문구도 없음): 업로드가 꺼진 빌드·실패한 회차·옛
 * 회차는 정상적으로 이 상태이고(ADR-0007), 못 들려줄 이유를 사용자에게 설명할 일이 아니다.
 * 반대로 **눌러서 시작한 재생이 실패하면 말한다**(토스트) — 사용자가 한 행동의 결과다.
 *
 * `analyId`는 **조회 시점의 저장본**에서 읽는다. 회차 마감 뒤 레코드가 한 번 더 저장되는데
 * (`useRecordMachine`: 마감이 다른 루트에서 끝나면 id 를 고쳐 쓴다) 그 전에 쥔 사본의 id 는
 * 죽은 루트일 수 있다 — 호출부가 어떤 사본을 들고 있든 같은 값을 쓰게 id 만 받는다.
 *
 * `<audio>`는 카드에 **하나**다 — 행마다 두면 동시 재생을 막는 일이 엘리먼트 사이의 조율이
 * 되고, 하나면 `src`를 바꾸는 것이 곧 앞의 재생을 끊는 것이다. WebAudio 는 쓰지 않는다
 * (`SongReferenceAudio` 주석의 금지 사유 그대로).
 */

const ReplayList = ({ media }: { media: TakeMedia[] }) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLAudioElement>(null);
  const [current, setCurrent] = useState<AnalyStereo | null>(null);
  // 가장 최근 재생 시도의 번호 — 갈아타거나 멈출 때 앞 시도의 `play()` 가 AbortError 로
  // 거절되는데, 그것은 실패가 아니라 우리가 끊은 것이다.
  const attempt = useRef(0);

  // 엘리먼트는 마운트 때 잡아 둔다 — 정리 시점에는 ref 가 이미 비어 있을 수 있다.
  useEffect(() => {
    const audio = ref.current;
    return () => audio?.pause();
  }, []);

  const fail = (): void => {
    attempt.current += 1;
    setCurrent(null);
    toast.error(t('web.result.replay.failed'));
  };

  const toggle = ({ stereo, url }: TakeMedia): void => {
    const audio = ref.current;
    if (!audio) {
      return;
    }
    attempt.current += 1;
    audio.pause();
    if (current === stereo) {
      setCurrent(null);
      return;
    }
    const mine = attempt.current;
    audio.src = url;
    setCurrent(stereo);
    // 거절을 삼키지 않는다 — 누른 사용자는 소리가 안 나는 이유를 알아야 한다.
    void audio.play().catch(() => {
      if (attempt.current === mine) {
        fail();
      }
    });
  };

  return (
    <Card className="px-5 pb-1 pt-[18px]">
      <h3 className="text-lg font-semibold tracking-[-0.1px] text-bk-900">
        {t('web.result.replay.title')}
      </h3>
      <ul className="mt-2 divide-y divide-bk-50">
        {media.map((take) => {
          const playing = current === take.stereo;
          const name = t(`web.diagnosis.tests.${take.stereo}.title`);
          return (
            <li key={take.stereo}>
              <button
                type="button"
                onClick={() => toggle(take)}
                aria-pressed={playing}
                aria-label={t(
                  playing ? 'web.result.replay.stop' : 'web.result.replay.play',
                  { take: name },
                )}
                className="flex min-h-[44px] w-full items-center gap-3 py-2 text-left text-base text-bk-900"
              >
                {playing ? (
                  <PauseButtonIcon className="h-6 w-6" aria-hidden="true" />
                ) : (
                  <PlayButtonIcon className="h-6 w-6" aria-hidden="true" />
                )}
                {name}
              </button>
            </li>
          );
        })}
      </ul>
      <audio
        ref={ref}
        preload="none"
        onEnded={() => setCurrent(null)}
        // 멈춘 뒤에 늦게 터진 로드 에러는 사용자가 이미 떠난 재생이다 — 말하지 않는다.
        onError={() => {
          if (current !== null) {
            fail();
          }
        }}
        aria-hidden="true"
      />
    </Card>
  );
};

export const ReplayCard = ({ recordId }: { recordId: string }) => {
  const analyId = useMemo(() => loadDiagnosis(recordId)?.analyId, [recordId]);
  const media = useTakeMedia(analyId);

  return media.length > 0 ? <ReplayList media={media} /> : null;
};
